import { S, Icon, formatDate, formatDateTime } from '../regureStyles';
import { getInitials } from '../../utils/gernalUtils';
import { moduleLabel, composedLogAction } from '../../utils/i18nLabels';

// Business event types the Recent Activity feed should show. The Log
// collection also contains 'audit' rows (raw HTTP requests like
// "GET /api/v1/claims/dashboard") — those must be excluded so the feed
// reads as business activity, not HTTP traffic. See logIt() types.
const BUSINESS_LOG_TYPES = new Set([
  'convert', 'update', 'create', 'delete', 'assign', 'reserve',
  'forgot-password', 'login', 'logout',
  'workflow', 'claims', 'claim', 'policies', 'policy',
  'offer', 'offers', 'commission', 'document', 'documents',
  'reggie', 'bordereau', 'bordereaux', 'invoice',
]);
const isBusinessEvent = (a) => {
  if (a?.isLive) return true; // socket-pushed live events are always business
  if (!a) return false;
  if (a.type === 'audit') return false;
  // Any GET reads that leaked past the type filter also excluded.
  const action = String(a.action || '');
  if (/^\s*(GET|OPTIONS|HEAD)\s+/i.test(action)) return false;
  if (BUSINESS_LOG_TYPES.has(a.type)) return true;
  // If type is empty/'action' but the action string is human-readable
  // (not an HTTP method + path), allow it through.
  return !/^(GET|POST|PATCH|PUT|DELETE)\s+\/api/i.test(action);
};

// Renewal color coding — matches the previous mock's thresholds.
const deadlineColor = (days) => {
  if (days <= 2)  return '#ef4444';
  if (days <= 14) return '#f59e0b';
  if (days <= 45) return '#3b82f6';
  return '#10b981';
};
// Renewal dates follow the tenant's configured ordering like every other
// field value. This used to hand-build `DD.MM.YYYY` with dot separators —
// a Swiss format asserted on every tenant regardless of configuration.
const formatEndDate = (iso) => formatDate(iso);

// HOW TALL A DASHBOARD CARD IS ALLOWED TO GET.
//
// Recent Activity has always been bounded — it slices to 8 rows and the card
// clips. Upcoming Deadlines was not: it rendered every row the API returned,
// and GET /policies/upcoming-deadlines returns up to 20. At ~66pt a row that
// is a 1,300pt card sitting in a two-column grid beside a 400pt one, so the
// grid row stretched to the taller card, the dashboard grew a page of its
// own, and there was no scrollbar to say any of it was happening.
//
// 380 shows five and a bit rows. The half-row is deliberate: a row cut by the
// fold is the cheapest honest signal that the list continues, and it works
// before the user has touched anything. The count beside the heading says how
// much more there is.
const DEADLINE_LIST_MAX_H = 380;

const ActivityAndDeadline = ({ t, history, liveActivity, logsList, upcomingDeadlines }) => {
  const rawFeed = (logsList?.userInfo?.data?.result) || [];
  // liveActivity now arrives already merged with the dashboard API's
  // recentActivity rows (see useDashboardFunctions). The audit-log feed is
  // still appended for tenants whose logs carry real business events.
  const merged = [...liveActivity, ...rawFeed].filter(isBusinessEvent).slice(0, 8);
  const deadlines = Array.isArray(upcomingDeadlines) ? upcomingDeadlines : [];
  // Rows the red dot applies to — the same threshold deadlineColor uses, so
  // the count and the dots can never disagree about what "urgent" means.
  const urgentCount = deadlines.filter(d => Number(d?.days) <= 2).length;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* Recent Activity */}
      <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, padding: 24, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('dashboard.recentActivity')}</div>
            {liveActivity.some(a => a?.isLive) && (
              <span
                style={{
                  padding: '2px 7px',
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#15803d',
                }}
              >
                {t('dashboard.live')}
              </span>
            )}
          </div>
          <button
            onClick={() => history.push('/audit-logs')}
            style={{ fontSize: 12, color: S.burgundy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
          >
            {t('dashboard.viewAll')}
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {merged.length === 0 ? (
            <div style={{ fontSize: 12, color: S.textMuted, padding: '20px 4px' }}>
              {t('dashboard.noRecentActivity', { defaultValue: 'No recent activity' })}
            </div>
          ) : merged.map((a, idx) => {
            // An activity row has an actor or it does not. A case nobody
            // has been assigned to has genuinely nobody to name, and the
            // feed used to fill that hole with a literal "-": every row
            // ended "… by -", and the avatar tile showed a dash. Neither
            // the clause nor the initials are rendered when there is no
            // actor — the event itself is still the point of the row.
            const actor = typeof a?.user?.name === 'string' ? a.user.name.trim() : '';
            return (
            <div key={a._id || idx} className={a.isLive ? 'dash-new-item' : ''} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {actor ? getInitials(actor) : <Icon name='activity' color='#fff' size={14} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: S.textPrimary, lineHeight: 1.4 }}>
                  {/* Audit rows carry the backend's English prose in `action`
                      ("Viewed users"). Where the row also has actionTags, the
                      sentence is composed in the reader's language instead;
                      otherwise the prose stands, since it names the entity. */}
                  <span style={{ color: S.textSecondary }}>{composedLogAction(t, a) || a.action}</span>{' '}
                  {/* `module` is a raw API slug. Rendering it uppercased put
                      "[CLAIMS]" in front of a French user on an otherwise
                      localised card — and did so for every module the audit
                      feed emits. Route it through the tenant's own vocabulary,
                      the same way statusLabel() does in useDashboardFunctions. */}
                  {a?.module && <strong style={{ fontWeight: 600, color: S.burgundy }}>[{moduleLabel(t, a.module).toUpperCase()}]</strong>}
                  {actor && <strong style={{ fontWeight: 600 }}>{' '}{t('dashboard.activityBy', { name: actor })}</strong>}
                </div>
                {/* One convention for the whole feed: an absolute timestamp in the
                    tenant's date order and timezone. formatHumanDate switched to
                    relative time under seven days and stamped ' UTC' beyond it, so
                    a single list interleaved "2 days ago" with
                    "13 Aug 2026 10:36 PM UTC" on a DD/MM/YYYY Europe/Madrid
                    tenant — two formats and a zone the tenant does not use. */}
                <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>{formatDateTime(a.updatedAt || a.createdAt)}</div>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming Deadlines */}
      <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('dashboard.upcomingDeadlines')}</div>
            {/* How many there are, stated rather than left to be discovered by
                scrolling. Turns red when something falls due inside two days,
                which is the one case where the count alone is not enough. */}
            {deadlines.length > 0 && (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 700,
                  color: urgentCount > 0 ? '#b91c1c' : S.textMuted,
                  background: urgentCount > 0 ? '#fef2f2' : S.bgCard,
                  border: `1px solid ${urgentCount > 0 ? '#fecaca' : S.borderLight}`,
                }}
              >
                {/* `total`, NOT `count`. i18next treats `count` as the
                    pluralisation selector and would look for
                    deadlineCountUrgent_one / _other — which this key does not
                    have. The same file already carries volumeTooltip_one /
                    _other, so that machinery is live here. */}
                {urgentCount > 0
                  ? t('dashboard.deadlineCountUrgent', {
                    defaultValue: '{{total}} · {{urgent}} due now',
                    total: deadlines.length,
                    urgent: urgentCount,
                  })
                  : deadlines.length}
              </span>
            )}
          </div>
          <button style={{ fontSize: 12, color: S.burgundy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
            <Icon name='calendar' color={S.burgundy} size={14} /> {t('dashboard.calendar')}
          </button>
        </div>
        {/* The list scrolls; the CARD does not grow. The scrollbar gutter is
            reserved by .dash-scroll, so the rows sit clear of the bar rather
            than running underneath it — no manual padding needed, and none
            that would shift when the bar appears. */}
        <div
          className={deadlines.length ? 'dash-scroll' : undefined}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            maxHeight: DEADLINE_LIST_MAX_H,
          }}
        >
          {deadlines.length === 0 ? (
            <div style={{ fontSize: 12, color: S.textMuted, padding: '20px 4px' }}>
              {t('dashboard.noUpcomingDeadlines', { defaultValue: 'No upcoming renewals in the next 90 days' })}
            </div>
          ) : deadlines.map(d => {
            const color = deadlineColor(d.days);
            return (
              <div
                key={d._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 10,
                  background: d.days <= 2 ? `${color}0a` : S.bgCard,
                  border: `1px solid ${d.days <= 2 ? color + '30' : S.borderLight}`,
                }}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: S.textPrimary }}>{d.label}</div>
                  <div style={{ fontSize: 11, color: S.textMuted, marginTop: 1 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color, marginRight: 6 }}>
                      {t(`deadline.${(d.type || 'renewal').toLowerCase()}`, { defaultValue: d.type || 'Renewal' })}
                    </span>
                    {formatEndDate(d.endDate)}
                    {d.insured && d.insured !== '—' ? ` · ${d.insured}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: d.days <= 2 ? color : S.textMuted, whiteSpace: 'nowrap' }}>
                  {d.days === 0 ? t('dashboard.today', { defaultValue: 'Today' }) : d.days === 1 ? t('dashboard.tomorrow') : `${d.days}d`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ActivityAndDeadline;
